// Achievement tracking hooks - wire into game systems
import { useEffect } from 'react'
import { useGameStore } from '@/store/gameStore'
import { useAchievementsStore } from '@/store/achievementsStore'
import { EventBus } from '@/game/utils/EventBus'

export function useAchievementTracking() {
  const { 
    incrementStat, 
    updateStat, 
    checkAchievements,
    stats,
  } = useAchievementsStore()
  
  const {
    money,
    reputation,
    courts,
    buildings,
    staff,
    day,
    totalIncome,
    totalExpenses,
    totalCustomers,
    tournamentsCompleted,
    players,
    leagueHistory,
  } = useGameStore()

  // Track stats from game state
  useEffect(() => {
    updateStat('courtsBuilt', courts.length)
    updateStat('buildingsBuilt', buildings.length)
    updateStat('staffHired', staff.length)
    updateStat('daysPlayed', day)
    updateStat('totalMoneyEarned', totalIncome)
    updateStat('totalMoneySpent', totalExpenses)
    updateStat('customersServed', totalCustomers)
    updateStat('tournamentsHosted', tournamentsCompleted)
    updateStat('leaguePromotions', leagueHistory.filter(h => h.position <= 2 && h.division > 1).length)
    updateStat('leagueTitles', leagueHistory.filter(h => h.position === 1).length)

    // Track max values
    if (players.length > stats.playersSigned) {
      updateStat('playersSigned', players.length)
    }
    if (money > stats.maxBalance) {
      updateStat('maxBalance', money)
    }
    if (reputation > stats.maxReputation) {
      updateStat('maxReputation', reputation)
    }

    // Check for new achievements
    const newAchievements = checkAchievements()

    // Apply rewards and notify (the celebration layer is purely visual)
    newAchievements.forEach(achievement => {
      if (achievement.reward?.money) {
        useGameStore.getState().addMoney(achievement.reward.money, `Logro: ${achievement.name}`)
      }
      EventBus.emit('achievement:unlocked', achievement)
    })
  }, [
    money,
    reputation,
    courts,
    buildings,
    staff,
    day,
    totalIncome,
    totalExpenses,
    totalCustomers,
    tournamentsCompleted,
    players,
    leagueHistory,
    updateStat,
    checkAchievements,
    stats.maxBalance,
    stats.maxReputation,
    stats.playersSigned,
  ])
  
  // Track specific events
  useEffect(() => {
    const handleEventResolved = () => {
      incrementStat('eventsResolved')
    }
    
    EventBus.on('event:resolved', handleEventResolved)
    return () => {
      EventBus.off('event:resolved', handleEventResolved)
    }
  }, [incrementStat])
}
